import { isTottusStandalone, isWhitelabel } from '@digital-retail/store-manager';
import { cx } from 'cvb';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { AdditionalPDPLabel } from '../../../../components/AdditionalPDPLabel/AdditionalPDPLabel';
import Availability from '../../../../components/Availability/Availability';
import { AvailabilityLocation } from '../../../../components/Availability/AvailabilityLocation/AvailabilityLocation';
import { Badges } from '../../../../components/Badges/Badges';
import CMRApertura from '../../../../components/CMRApertura/CMRApertura';
import CMRCalculatorDesktop from '../../../../components/CMRCalculator/Desktop';
import FloorCalculator from '../../../../components/FloorCalculator/FloorCalculator';
import FloorCalculatorWarning from '../../../../components/FloorCalculator/FloorCalculatorWarning/FloorCalculatorWarning';
import InternationalShipping from '../../../../components/InternationalShipping/InternationalShipping';
import MultipurposeBadge from '../../../../components/MultipurposeBadge/MultipurposeBadge';
import Operator from '../../../../components/Operator/Operator';

import {
  getDeliveryOptionBorderClassName,
  getZoneName,
} from '../../../../components/Availability/DeliveryOptions/DeliveryOptions.Helper';
import InstallmentsWithoutInterest from '../../../../components/InstallmentsWithoutInterest/InstallmentsWithoutInterest';
import ProductColorSwatches from '../../../../components/ProductColorSwatches/ProductColorSwatches';
import ProductPrices from '../../../../components/ProductPrices/ProductPrices';
import ProductServices from '../../../../components/ProductServices/ProductServices';
import ProductTopSpecifications from '../../../../components/ProductTopSpecifications/ProductTopSpecifications';
import { ReaconditionedContainer } from '../../../../components/ReaconditionedContainer/ReaconditionedContainer';
import SellerInfo from '../../../../components/SellerInfo/SellerInfo';
import SellerRatings from '../../../../components/SellerRatings/SellerRatings';
import { ShippingDetails } from '../../../../components/ShippingDetails/ShippingDetails';
import ShippingTooltip from '../../../../components/ShippingTooltip/ShippingTooltip';
import Size from '../../../../components/Size/Size';
import StockQuantity from '../../../../components/StockQuantity/StockQuantity';
import Warranty from '../../../../components/Warranty/Warranty';
import BodyCopy from '../../../../components/ui/BodyCopy/BodyCopy';
import Button from '../../../../components/ui/Button/Button';
import Stepper from '../../../../components/ui/Stepper/Stepper';
import StockTable from '../../../../components/ui/StockTable/StockTable';
import constants from '../../../../config/constants';
import { withApplicationContext } from '../../../../utils/ApplicationContext';
import { withCart } from '../../../../utils/CartContext';
import { withLabels } from '../../../../utils/LabelsContext';
import _ from '../../../../utils/LodashImports';
import { withProduct } from '../../../../utils/ProductContext';
import { withUser } from '../../../../utils/UserContext';
import { useUserSegments } from '../../../../utils/hooks/useUserSegments';
import { noop } from '../../../../utils/noop';
import * as productUtils from '../../../../utils/product/productUtils';
import { getVariant } from '../../../../utils/variant';
import ProductQuantitySelectionContainer from './ProductQuantitySelectionContainer';
import { ProductSpecificationsDesktopStyle } from './ProductSpecificationsDesktop.style';

const ProductSpecificationsDesktop = ({
  product,
  labels,
  appCtx,
  isPumAvailable,
  pumQuantity,
  primeShipping,
  colorVariants,
  selectedColorSwatchIndex,
  colorSwatchHandler,
  sizes,
  selectedSize,
  sizeChangeHandler,
  handleAddToCart,
  handleRemoveFromCart,
  isRemoveFromCartInProgress,
  isAddToCartInProgress,
  operators,
  operatorChangeHandler,
  addToCartButtonVisible,
  showCollectionButtonVisible,
  setWarrantyOption,
  setServiceOption,
  isVendorLogin,
  storeSkuData,
  currentTab,
  selectedOperatorId,
  errorAddingToCart,
  requestProductFromStore,
  handleM2QuantityChange,
  m2Quantity,
  m2ResultValue,
  handleM2ResultChange,
  m2Value,
  handleM2ValueChange,
  isExtraM2Checked,
  handleExtraM2Change,
  m2AttributeValue,
  setShowLoginForm,
  showLoginForm,
  setErrorAddingToCart,
  sizeChartData,
  fomo,
  isDeliveryTypeTooltipEnabled,
  onCloseShippingTooltip,
  deliveryTypeTooltip,
  userSegments,
  isUserLoggedIn,
  variantSwatchType,
}) => {
  const { regionCode, store, siteConfig, comuna: comunaData, isDefaultComuna } = appCtx || {};
  const { MAX_CART_PRODUCT, FIRST_PARTY_SELLER_REGEX, PRODUCT_CONSTANTS, VIEW_TEMPLATE, DEVICE_DESKTOP } = constants;
  const { COLOR, SEE_COLLECTION_BUTTON_LABEL, PDP_STARTS_FROM, CMR_INSTALLMENT_ERROR_MESSAGE } = labels;

  const {
    variants,
    id: productId,
    currentVariant: currentVariantId,
    additionalPDPLabels,
    isOutOfStock,
    colorMetrics,
    darSectionActive = false,
    cmrBannerActive = true,
    serviceOptions,
    warrantyOptions,
    attributes,
    merchantCategoryId,
    isPublished,
    sellerInfo,
  } = product;

  const {
    badges = [],
    meatStickers = [],
    promotions,
    prices,
    cmrPoints,
    discountBadge,
    cmrSavings,
    maxOnCartAmount = MAX_CART_PRODUCT,
    multipurposeBadges = [],
    availabilityLabels = {},
  } = getVariant(variants, currentVariantId);

  const isConnectProduct = productUtils.isConnectProduct(product);

  const { currentUserSegments } = useUserSegments(regionCode, userSegments, isUserLoggedIn, appCtx?.store);

  const { viewTemplate, pumUnits, isPrimeAvailable } = attributes;

  const allowed3PCampaignNamesToRender = _.get(
    siteConfig,
    'textDictionary.ALLOWED_3P_CAMPAIGN_NAMES_TO_RENDER',
    'ENVIO GRATIS 3P, ENVIO_GRATIS_3P'
  );

  const {
    isImprovedWarrantyUiEnabled = true,
    isImprovedServicesUiEnabled = true,
    isFloorcalculatorEnabled = true,
    isSellerRatingsEnabled = true,
    isInternationalBuyVisiblePDP = false,
    isStoreAvailabilityVisibleInPDP = false,
    isQuantityEditableEnabled = false,
    isWarrantyTooltipEnabled = false,
    isCMRCalculatorEnabledPDP = false,
    isMultipurposeBadgeApplicable = false,
    isCMRAperturaEnabled = false,
    isMeatStickerApplicableInPDP = false,
  } = _.get(siteConfig, 'toggles', {});

  const freeShippingPromotionFor3p = promotions?.find((promotion) =>
    allowed3PCampaignNamesToRender
      .split(',')
      .map((name) => name.trim())
      .includes(promotion.campaignName)
  );
  const hasStickers = (isMeatStickerApplicableInPDP && meatStickers.length > 0) || freeShippingPromotionFor3p;
  const nonEventMeatStickers = (meatStickers || []).filter((sticker) => !['event'].includes(sticker.type));
  const sellerName = _.get(variants, '[0].offerings[0].sellerName', '');
  const is1pSeller = FIRST_PARTY_SELLER_REGEX.test(sellerName.toLowerCase());
  const type =
    product.productType === PRODUCT_CONSTANTS.CONNECT ? PRODUCT_CONSTANTS.CONNECT : PRODUCT_CONSTANTS.HARDLINE;

  const shouldRenderSize = viewTemplate !== VIEW_TEMPLATE.TYPE_A && sizes.length > 0;

  const isStoreDataVaild = storeSkuData && Object.keys(storeSkuData).length > 0;

  const addToCartLabel = _.get(additionalPDPLabels, 'addToCart.enabled', false)
    ? _.get(additionalPDPLabels, 'addToCart', {})
    : null;

  const additionalWarrantyLabel = _.get(additionalPDPLabels, 'warranty.enabled', false)
    ? _.get(additionalPDPLabels, 'warranty', {})
    : null;

  const isReaconditionedProduct = productUtils.isReaconditionedProduct(product);
  const isWhitelabelSite = isWhitelabel({ store });
  const isTottusSA = isTottusStandalone(store);

  const selectedZone = comunaData?.data || null;
  const defaultZoneConfig =
    selectedZone && !_.isEmpty(selectedZone)
      ? selectedZone
      : {
          region: {},
          comuna: {},
        };

  const [zoneConfig, setZoneConfig] = useState(defaultZoneConfig);

  const deliveryOptionsBorderClassName = getDeliveryOptionBorderClassName(isWhitelabelSite);

  const expressDeliveryLimit = _.get(variants, '[0].expressDeliveryLimit', 0);

  useEffect(() => {
    if (selectedZone) setZoneConfig({ ...selectedZone });
  }, [selectedZone]);

  const { comunaName } = getZoneName(selectedZone, isDefaultComuna) || { comunaName: '' };

  if (isOutOfStock) {
    return (
      <div
        className="product-specifications-out-of-stock"
        data-backend-category={merchantCategoryId}
        data-availability={attributes && isPrimeAvailable}
        data-published={isPublished}
        data-variations={variants.length}
      >
        {prices && prices.length > 0 && (
          <div className="price">
            <ProductPrices
              isVendorLogin={isVendorLogin}
              currentTab={currentTab}
              storeSkuData={storeSkuData}
              prices={productUtils.getPriceForOutOfStock(prices)}
              productId={productId}
              regionCode={regionCode}
              cmrPoints={cmrPoints}
              pumUnit={pumUnits}
              isPumAvailable={isPumAvailable}
              pumQuantity={pumQuantity}
              primeShipping={primeShipping}
              deviceType={DEVICE_DESKTOP}
              operators={operators}
              selectedOperatorId={selectedOperatorId}
              isConnectProduct={isConnectProduct}
            />
          </div>
        )}

        <SellerInfo product={product} />
        {isReaconditionedProduct && <ReaconditionedContainer appCtx={appCtx} productAttributes={product} />}
        <style jsx>{ProductSpecificationsDesktopStyle}</style>
      </div>
    );
  }
  const sizeSelectionLabel = productUtils.getSizeSelectionLabel(product, labels.SELECTION_SIZE);
  return (
    <div
      className="product-specifications"
      data-backend-category={merchantCategoryId}
      data-availability={attributes && isPrimeAvailable}
      data-published={isPublished}
      data-variations={variants.length}
    >
      <div className="product-specifications-column fa--product-specifications-column__desktop">
        <SellerInfo product={product} />
        {colorVariants[selectedColorSwatchIndex] && (
          <div className="color-swatch-container fa--color-swatches__desktop">
            <BodyCopy size="copy3" isHighlighted>
              {`${COLOR}: `}
            </BodyCopy>
            <BodyCopy size="copy3">{colorVariants[selectedColorSwatchIndex].label}</BodyCopy>
            <ProductColorSwatches
              colorVariants={colorVariants}
              productId={product.id}
              selectedColorSwatchIndex={selectedColorSwatchIndex}
              colorSwatchHandler={colorSwatchHandler}
              type={type}
              size="medium"
              appCtx={appCtx}
              colorMetrics={colorMetrics}
              variantSwatchType={variantSwatchType}
            />
          </div>
        )}
        {shouldRenderSize && (
          <Size
            title={sizeSelectionLabel}
            sizes={sizes}
            selectedSize={selectedSize}
            onSizeChange={sizeChangeHandler}
            sizeChartData={sizeChartData}
            fomo={fomo}
          />
        )}
        {isVendorLogin && isStoreDataVaild && (
          <StockTable
            stockData={storeSkuData}
            colorVariants={colorVariants}
            selectedColorSwatchIndex={selectedColorSwatchIndex}
            sizes={sizes}
            requestProductFromStore={requestProductFromStore}
            regionCode={regionCode}
          />
        )}
        <ProductTopSpecifications scollToElementId="productInfoContainer" />
        {!!product.showShippingModal && (
          <div className={cx('delivery-options-wrapper', deliveryOptionsBorderClassName)}>
            {selectedZone && (
              <div className="avl-content">
                <AvailabilityLocation comunaName={comunaName} />
              </div>
            )}
            <Availability
              sizes={sizes}
              colorVariants={colorVariants}
              regionCode={regionCode}
              store={store}
              isStoreAvailabilityVisibleInPDP={isStoreAvailabilityVisibleInPDP}
              is1pSeller={is1pSeller}
              selectedZone={selectedZone}
              zoneConfig={zoneConfig}
              setZoneConfig={setZoneConfig}
              isDefaultComuna={isDefaultComuna}
              comunaName={comunaName}
            />
          </div>
        )}
        {isReaconditionedProduct && <ReaconditionedContainer appCtx={appCtx} productAttributes={product} />}
      </div>

      <div className="product-specifications-column fa--product-specifications-column__desktop">
        <div
          className={`price
        ${isMultipurposeBadgeApplicable && multipurposeBadges.length > 0 ? 'withMultipurposeBadge' : ''}`}
        >
          <ProductPrices
            isVendorLogin={isVendorLogin}
            currentTab={currentTab}
            storeSkuData={storeSkuData}
            prices={prices}
            productId={productId}
            regionCode={regionCode}
            cmrPoints={cmrPoints}
            pumUnit={pumUnits}
            isPumAvailable={isPumAvailable}
            pumQuantity={pumQuantity}
            primeShipping={primeShipping}
            deviceType={DEVICE_DESKTOP}
            operators={operators}
            selectedOperatorId={selectedOperatorId}
            isConnectProduct={isConnectProduct}
            discountBadge={discountBadge}
            darSectionActive={darSectionActive}
          />
          <InstallmentsWithoutInterest
            appCtx={appCtx}
            product={product}
            userSegments={currentUserSegments}
            isUserLoggedIn={isUserLoggedIn}
          />
        </div>
        {isInternationalBuyVisiblePDP && <InternationalShipping product={product} />}
        {(badges?.length || meatStickers?.length || freeShippingPromotionFor3p?.badge) && (
          <div className="meatstickers-container">
            <Badges
              badgeItems={isMeatStickerApplicableInPDP ? nonEventMeatStickers : badges}
              layout="PDP"
              isSticker={hasStickers}
              promotionBadge={freeShippingPromotionFor3p?.badge}
            />
          </div>
        )}
        {isMultipurposeBadgeApplicable && multipurposeBadges.length > 0 && (
          <div className="multipurpose-badge">
            <MultipurposeBadge badgeItems={multipurposeBadges} deviceType="desktop" inPdp />
          </div>
        )}
        <StockQuantity />
        {isConnectProduct && (
          <Operator
            isDesktop
            isConnectProduct={isConnectProduct}
            operators={operators}
            operatorChangeHandler={operatorChangeHandler}
            selectedOperatorId={selectedOperatorId}
          />
        )}
        {addToCartLabel && (
          <AdditionalPDPLabel label={addToCartLabel} type="addToCart" productType="hardline" desktop />
        )}
        {m2AttributeValue > 0 && isFloorcalculatorEnabled && (
          <div className="floorCalc-pdp-container">
            <FloorCalculator
              appCtx={appCtx}
              m2AttributeValue={m2AttributeValue}
              handleM2QuantityChange={handleM2QuantityChange}
              handleM2ResultChange={handleM2ResultChange}
              m2Value={m2Value}
              m2ResultValue={m2ResultValue}
              handleM2ValueChange={handleM2ValueChange}
              isExtraM2Checked={isExtraM2Checked}
              handleExtraM2Change={handleExtraM2Change}
            />
            <div className="quantity-selector">
              <Stepper
                type="secondary"
                inputType="text"
                minimum={1}
                maximum={maxOnCartAmount}
                count={m2Quantity}
                incrementButtonId="testId-floorCalc-stepper-increment-btn"
                decrementButtonId="testId-floorCalc-stepper-decrement-btn"
                editable={isQuantityEditableEnabled}
                onInputValueChange={handleM2QuantityChange}
                inputMaxLength={3}
              />
            </div>
            {m2ResultValue > 0 && m2Quantity < m2ResultValue && (
              <FloorCalculatorWarning
                appCtx={appCtx}
                isExtraM2Checked={isExtraM2Checked}
                m2Value={m2Value}
                m2ResultValue={m2ResultValue}
                m2Quantity={m2Quantity}
              />
            )}
          </div>
        )}
        {isTottusSA && availabilityLabels && <ShippingDetails availabilityLabels={availabilityLabels} />}
        {expressDeliveryLimit > 0 && (
          <ShippingTooltip
            isOpen={isDeliveryTypeTooltipEnabled}
            deliveryType={deliveryTypeTooltip}
            onClose={onCloseShippingTooltip}
          />
        )}
        <ProductQuantitySelectionContainer
          errorAddingToCart={errorAddingToCart}
          addToCartButtonVisible={addToCartButtonVisible}
          isAddToCartInProgress={isAddToCartInProgress}
          labels={labels}
          maxCartQuantity={maxOnCartAmount}
          handleAddToCart={handleAddToCart}
          handleRemoveFromCart={handleRemoveFromCart}
          isRemoveFromCartInProgress={isRemoveFromCartInProgress}
          isVendorLogin={isVendorLogin}
          sizes={sizes}
          selectedSize={selectedSize}
          setShowLoginForm={setShowLoginForm}
          showLoginForm={showLoginForm}
          setErrorAddingToCart={setErrorAddingToCart}
          m2AttributeValue={m2AttributeValue}
          layout="hardline"
        />
        {showCollectionButtonVisible && (
          <div className="addToCart-container fa--add-to-cart__desktop">
            <Button type="outline" onClick={handleAddToCart} size="xtra-large">
              <span>
                {SEE_COLLECTION_BUTTON_LABEL}
                <i className="csicon-arrow_down_small" />
              </span>
            </Button>
          </div>
        )}
        <Warranty
          warrantyOptions={warrantyOptions}
          setWarrantyOption={setWarrantyOption}
          startsFromLabel={PDP_STARTS_FROM}
          tooltipToggle={isWarrantyTooltipEnabled}
          additionalWarrantyLabel={additionalWarrantyLabel}
          sellerName={sellerName}
        />
        {isImprovedWarrantyUiEnabled && isImprovedServicesUiEnabled && <div className="services-divider" />}
        <ProductServices serviceOptions={serviceOptions} setServiceOption={setServiceOption} />

        {isCMRAperturaEnabled && cmrBannerActive && (
          <div className="cmrApertura-container">
            <CMRApertura prices={prices} cmrSavings={cmrSavings} />
          </div>
        )}
        {isCMRCalculatorEnabledPDP && (
          <CMRCalculatorDesktop errorMessage={CMR_INSTALLMENT_ERROR_MESSAGE} layout="hardline" />
        )}
        {isSellerRatingsEnabled && sellerInfo?.rating && <SellerRatings sellerInfo={sellerInfo} />}
      </div>
      <style jsx>{ProductSpecificationsDesktopStyle}</style>
    </div>
  );
};

ProductSpecificationsDesktop.defaultProps = {
  isPumAvailable: false,
  pumQuantity: 0,
  primeShipping: false,
  colorVariants: [],
  selectedColorSwatchIndex: 0,
  colorSwatchHandler: noop,
  sizes: [],
  selectedSize: '',
  sizeChangeHandler: noop,
  handleAddToCart: noop,
  isAddToCartInProgress: false,
  handleRemoveFromCart: noop,
  isRemoveFromCartInProgress: false,
  operators: [],
  operatorChangeHandler: noop,
  addToCartButtonVisible: true,
  showCollectionButtonVisible: false,
  setWarrantyOption: noop,
  setServiceOption: noop,
  requestProductFromStore: noop,
  storeSkuData: {},
  currentTab: '',
  selectedOperatorId: null,
  errorAddingToCart: false,
  handleM2QuantityChange: noop,
  m2Quantity: 1,
  m2ResultValue: 0,
  handleM2ResultChange: noop,
  m2Value: '',
  handleM2ValueChange: noop,
  isExtraM2Checked: true,
  handleExtraM2Change: noop,
  m2AttributeValue: -1,
  showLoginForm: false,
  sizeChartData: {},
  fomo: {},
  isDeliveryTypeTooltipEnabled: false,
  onCloseShippingTooltip: noop,
  deliveryTypeTooltip: '',
  userSegments: [],
  isUserLoggedIn: false,
  variantSwatchType: undefined,
};

ProductSpecificationsDesktop.propTypes = {
  product: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  isPumAvailable: PropTypes.bool,
  pumQuantity: PropTypes.number,
  primeShipping: PropTypes.bool,
  colorVariants: PropTypes.array,
  selectedColorSwatchIndex: PropTypes.number,
  colorSwatchHandler: PropTypes.func,
  sizes: PropTypes.array,
  selectedSize: PropTypes.string,
  sizeChangeHandler: PropTypes.func,
  handleAddToCart: PropTypes.func,
  isAddToCartInProgress: PropTypes.bool,
  handleRemoveFromCart: PropTypes.func,
  isRemoveFromCartInProgress: PropTypes.bool,
  operators: PropTypes.array,
  operatorChangeHandler: PropTypes.func,
  requestProductFromStore: PropTypes.func,
  addToCartButtonVisible: PropTypes.bool,
  showCollectionButtonVisible: PropTypes.bool,
  setWarrantyOption: PropTypes.func,
  setServiceOption: PropTypes.func,
  isVendorLogin: PropTypes.bool.isRequired,
  storeSkuData: PropTypes.object,
  currentTab: PropTypes.string,
  selectedOperatorId: PropTypes.string,
  errorAddingToCart: PropTypes.bool,
  handleM2QuantityChange: PropTypes.func,
  m2Quantity: PropTypes.number,
  m2ResultValue: PropTypes.number,
  handleM2ResultChange: PropTypes.func,
  m2Value: PropTypes.string,
  handleM2ValueChange: PropTypes.func,
  isExtraM2Checked: PropTypes.bool,
  handleExtraM2Change: PropTypes.func,
  m2AttributeValue: PropTypes.number,
  setShowLoginForm: PropTypes.func.isRequired,
  setErrorAddingToCart: PropTypes.func.isRequired,
  showLoginForm: PropTypes.bool,
  sizeChartData: PropTypes.object,
  fomo: PropTypes.object,
  isDeliveryTypeTooltipEnabled: PropTypes.bool,
  onCloseShippingTooltip: PropTypes.func,
  deliveryTypeTooltip: PropTypes.string,
  userSegments: PropTypes.array,
  isUserLoggedIn: PropTypes.bool,
  variantSwatchType: PropTypes.string,
};

export default withCart(withProduct(withLabels(withApplicationContext(withUser(ProductSpecificationsDesktop)))));
